import { useEffect, useState } from 'react';
import { ResolutionString } from '../../../charting_library';
import Button from '../../../components/Button';
import Card from '../../../components/Card';
import TVChartContainer from '../../../components/TVChart';
import useChartState from '../../../context/chart/useChartState';
import { formatUSD } from '../../../helpers';
import chartConfig from '../../../components/TVChart/chart.config';
import { convertInterval } from '../../../components/TVChart/chart.helpers';
import clsx from 'clsx';
import Spinner from '../../../components/Spinner';
import useTradePools from '../../../hooks/web3/trade/useTradePools';
import { ChartSymbol } from '../../../context/chart/chartTypes';

const initialOhlcState = { o: 0, h: 0, l: 0, c: 0 };

type ExchangeChartProps = {
    marketOptionsCollapsed: boolean;
};

const ExchangeChart: React.FC<ExchangeChartProps> = ({ marketOptionsCollapsed }) => {
    const { isChartReady, isAdvancedMode, tvWidget, favouriteMarkets } = useChartState();

    const { tradePools } = useTradePools();

    const [activeResolution, setActiveResolution] = useState('15' as ResolutionString);
    const [ohlc, setOhlc] = useState(initialOhlcState);
    const [initialChartSymbol, setInitialChartSymbol] = useState<ChartSymbol | undefined>(
        undefined
    );

    const handleResolutionClick = (resolution: ResolutionString) => {
        setActiveResolution(resolution);
        tvWidget?.activeChart().setResolution(resolution);
    };

    useEffect(() => {
        //  runs only once before initialChartSymbol is set
        if (!tradePools || initialChartSymbol) return;

        const sortedPools = tradePools
            .map((pool) => ({
                ...pool,
                isFavourited: favouriteMarkets.includes(pool.name),
            }))
            .sort(
                // sorts by favourite then, sorts by name
                (a, b) =>
                    (a.isFavourited === b.isFavourited ? 0 : a.isFavourited ? -1 : 1) ||
                    a.name.localeCompare(b.name)
            );

        setInitialChartSymbol(sortedPools[0].chartSymbol);
    }, [tradePools, favouriteMarkets, initialChartSymbol]);

    return (
        <div className={clsx('exchange-chart', { collapsed: marketOptionsCollapsed })}>
            <Card>
                {isChartReady ? (
                    <>
                        <div
                            className={clsx('extras-container', {
                                advanced: isAdvancedMode,
                            })}
                        >
                            <div className='interval-menu'>
                                {chartConfig.supported_resolutions.map((interval) => {
                                    const active = activeResolution === interval;

                                    return (
                                        <Button
                                            key={interval}
                                            onClick={
                                                tvWidget
                                                    ? () =>
                                                          handleResolutionClick(interval)
                                                    : undefined
                                            }
                                            className={clsx({
                                                active,
                                            })}
                                            tabIndex={active ? -1 : 0}
                                        >
                                            {convertInterval(interval).toLowerCase()}
                                        </Button>
                                    );
                                })}
                            </div>
                            <div
                                className={`ohlc-data ${ohlc.c > ohlc.o ? 'up' : 'down'}`}
                            >
                                {Object.entries(ohlc).map(([key, value]) => (
                                    <div key={key}>
                                        <div className='label'>{key.toUpperCase()}</div>
                                        <div className='value'>
                                            {formatUSD(value || 0, {
                                                currency: '',
                                                minDecimals: 2,
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className='loading-container'>
                        <Spinner />
                        <span>Initializing...</span>
                    </div>
                )}
                {initialChartSymbol ? (
                    <TVChartContainer
                        initialChartSymbol={initialChartSymbol}
                        setOhlc={setOhlc}
                    />
                ) : null}
            </Card>
        </div>
    );
};

export default ExchangeChart;
